import {CompanyContractAddendum, type CompanyContractAddendumData} from "@/types/company/CompanyContractAddendum.ts";
import {CompanyAnnex, type CompanyAnnexData} from "@/types/company/CompanyAnnex.ts";

export class CompanyContract {
    public id:number|null;
    public company_id:number;
    public contract_nr:number;
    public contract_period:number;
    public contract_type:string;
    public date_signed:Date|null;
    public renewal:number|null;
    public salary_clause:boolean;
    public fuel_clause:boolean;
    public modified_contract_clause:boolean;
    public right_of_retention:boolean;
    public right_of_pledge:boolean;
    public revoked:boolean;
    public revoked_by_user_id:number|null;
    public file_name:string|null;
    public file_content_base64:string|null;
    public companyContractAddendums: CompanyContractAddendum[];
    public activeCompanyContractAddendums: CompanyContractAddendum[];
    public companyAnnexes: CompanyAnnex[];
    public activeCompanyAnnexes: CompanyAnnex[];


    constructor(data:CompanyContractData|void) {
        if(data === undefined)
        {
            this.id = null;
            this.company_id = 0;
            this.contract_nr = 0;
            this.contract_period = 0;
            this.contract_type = "";
            this.date_signed = null;
            this.renewal = null;
            this.salary_clause = false;
            this.fuel_clause = false;
            this.modified_contract_clause = false;
            this.right_of_retention = false;
            this.right_of_pledge = false;
            this.revoked = false;
            this.revoked_by_user_id = 0;
            this.file_name = null
            this.file_content_base64 = null;
            this.companyContractAddendums = [];
            this.activeCompanyContractAddendums = [];
            this.companyAnnexes = [];
            this.activeCompanyAnnexes = [];

        }
        else
        {
            this.id = data.id;
            this.company_id = data.company_id;
            this.contract_nr = data.contract_nr;
            this.contract_period = data.contract_period;
            this.contract_type = data.contract_type;

            this.date_signed = null;
            if(data.date_signed !== null)
            {
                this.date_signed = new Date(data.date_signed);
            }
            this.renewal = data.renewal;
            this.salary_clause = data.salary_clause;
            this.fuel_clause = data.fuel_clause;
            this.modified_contract_clause = data.modified_contract_clause;
            this.right_of_retention = data.right_of_retention;
            this.right_of_pledge = data.right_of_pledge;
            this.revoked = data.revoked;
            this.revoked_by_user_id = data.revoked_by_user_id;
            this.file_name = data.file_name;
            this.file_content_base64 = data.file_content_base64 || null;

            this.companyContractAddendums = [];
            if (data.companyContractAddendums !== undefined) {
                data.companyContractAddendums.forEach(companyContractAddendum => this.companyContractAddendums.push((new CompanyContractAddendum(companyContractAddendum))));
            }

            this.activeCompanyContractAddendums = [];
            if (data.activeCompanyContractAddendums !== undefined) {
                data.activeCompanyContractAddendums.forEach(activeCompanyContractAddendum => this.activeCompanyContractAddendums.push((new CompanyContractAddendum(activeCompanyContractAddendum))));
            }

            this.companyAnnexes = [];
            if (data.companyAnnexes !== undefined) {
                data.companyAnnexes.forEach(companyAnnex => this.companyAnnexes.push((new CompanyAnnex(companyAnnex))));
            }

            this.activeCompanyAnnexes = [];
            if (data.activeCompanyAnnexes !== undefined) {
                data.activeCompanyAnnexes.forEach(activeCompanyAnnex => this.activeCompanyAnnexes.push((new CompanyAnnex(activeCompanyAnnex))));
            }

        }
    }

    serializeData(without_relations: boolean | Array<string> = false, with_relations: boolean | Array<string> = false, ids_only:boolean = false) {
        const serialized_data = {
            id : this.id,
            company_id : this.company_id,
            contract_nr : this.contract_nr,
            contract_period : this.contract_period,
            contract_type : this.contract_type,
            date_signed : this.date_signed,
            renewal: this.renewal,
            salary_clause: this.salary_clause,
            fuel_clause: this.fuel_clause,
            modified_contract_clause: this.modified_contract_clause,
            right_of_retention: this.right_of_retention,
            right_of_pledge: this.right_of_pledge,
            revoked: this.revoked,
            revoked_by_user_id: this.revoked_by_user_id,
            file_name: this.file_name,
            file_content_base64: this.file_content_base64,
        }
        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('companyContractAddendums')
            )
        ) {
            serialized_data.companyContractAddendums = [];
            this.companyContractAddendums.forEach(el => {
                serialized_data.companyContractAddendums.push(el.serializeData())
            })
        }

        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('activeCompanyContractAddendums')
            )
        ) {
            serialized_data.activeCompanyContractAddendums = [];
            this.activeCompanyContractAddendums.forEach(el => {
                serialized_data.activeCompanyContractAddendums.push(el.serializeData())
            })
        }

        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('companyAnnexes')
            )
        ) {
            serialized_data.companyAnnexes = [];
            this.companyAnnexes.forEach(el => {
                serialized_data.companyAnnexes.push(el.serializeData())
            })
        }

        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('activeCompanyAnnexes')
            )
        ) {
            serialized_data.activeCompanyAnnexes = [];
            this.activeCompanyAnnexes.forEach(el => {
                serialized_data.activeCompanyAnnexes.push(el.serializeData())
            })
        }

        return serialized_data;
    }

}

export type CompanyContractData = {
    id:number|null;
    company_id:number;
    contract_nr:number;
    contract_period:number;
    contract_type:string;
    date_signed:Date|null;
    renewal:number|null;
    salary_clause: boolean;
    fuel_clause: boolean;
    modified_contract_clause: boolean;
    right_of_retention: boolean;
    right_of_pledge: boolean;
    revoked:boolean;
    revoked_by_user_id:number|null;
    file_name:string|null;
    file_content_base64?:string|null;
    companyContractAddendums: CompanyContractAddendumData[] | undefined;
    activeCompanyContractAddendums: CompanyContractAddendumData[] | undefined;
    companyAnnexes: CompanyAnnexData[] | undefined;
    activeCompanyAnnexes: CompanyAnnexData[] | undefined;
}
